import { api } from "../../api"
import { TimeZoneType } from "../../constants/timezones"
import { NotificationChannel, NotificationType } from "../../notifications/notifications"
import { DateOrderOptionType, DateSeparatorOptionType } from "../../utils/date-format"
import { Language } from "../../utils/languages"
import { EmailChangeRequestResponse, EmailChangeRequestViewModel, makeEmailChangeRequestViewModel } from "./email"
import { makeUserApiEndpoints } from "./endpoints"
import { UserViewModel, makeUserViewModel } from "./user"

const apiEndpoints = makeUserApiEndpoints()

export const userApi = api.injectEndpoints({
  endpoints: (builder) => {
    return {
      getUserInfo: builder.query<UserViewModel, void>({
        query: () => apiEndpoints.userInfo(),
        transformResponse: makeUserViewModel,
        providesTags: ["User"],
      }),
      getEmailChangeRequest: builder.query<EmailChangeRequestViewModel | null, void>({
        query: () => apiEndpoints.userEmailChangeRequest(),
        transformResponse: (response: EmailChangeRequestResponse | null) => {
          return response ? makeEmailChangeRequestViewModel(response) : null
        },
        providesTags: ["EmailChangeRequest"],
      }),
      getPasswordResetToken: builder.query<void, { email: string; language: string }>({
        query: ({ email, language }) => ({
          url: apiEndpoints.userPasswordReset(),
          method: "GET",
          params: { email, language },
        }),
      }),
      updateUserInfo: builder.mutation<UserViewModel, { userId: string } & UserInfoUpdateData>({
        query: ({ userId, ...updateData }) => ({
          url: apiEndpoints.userItem(userId),
          method: "PUT",
          body: updateData,
        }),
        invalidatesTags: ["User"],
      }),
      updateUserEmail: builder.mutation<void, { userId: string } & UserEmailUpdateData>({
        query: ({ userId, ...updateData }) => ({
          url: apiEndpoints.userEmail(userId),
          method: "PUT",
          body: updateData,
        }),
        invalidatesTags: ["User", "EmailChangeRequest"],
      }),
      updateUserPassword: builder.mutation<void, { userId: string } & UserPasswordUpdateData>({
        query: ({ userId, ...updateData }) => ({
          url: apiEndpoints.userPassword(userId),
          method: "PUT",
          body: updateData,
        }),
      }),
      verifyEmail: builder.mutation<void, { userId: string } & UserEmailVerificationData>({
        query: ({ userId, ...updateData }) => ({
          url: apiEndpoints.userEmailVerify(userId),
          method: "PUT",
          body: updateData,
        }),
        invalidatesTags: ["User", "EmailChangeRequest"],
      }),
      resendOtp: builder.mutation<void, { userId: string }>({
        query: ({ userId }) => ({
          url: apiEndpoints.userEmailChangeRequest(userId),
          method: "PUT",
        }),
        invalidatesTags: ["EmailChangeRequest"],
      }),
      cancelEmailChangeRequest: builder.mutation<void, { userId: string }>({
        query: ({ userId }) => ({
          url: apiEndpoints.userEmailChangeRequest(userId),
          method: "DELETE",
        }),
        invalidatesTags: ["EmailChangeRequest"],
      }),
      updateNotificationSettings: builder.mutation<
        UserViewModel,
        { userId: string } & UserNotificationSettingsUpdateData
      >({
        query: ({ userId, ...updateData }) => ({
          url: apiEndpoints.userNotificationSettings(userId),
          method: "PUT",
          body: updateData,
        }),
        invalidatesTags: ["User"],
      }),
      sendWelcomeEmail: builder.query<void, { type: WelcomeEmailType; orgName?: string }>({
        query: ({ type, orgName }) => ({
          url: apiEndpoints.sendWelcomeEmail(),
          method: "POST",
          body: { type, orgName },
        }),
      }),
    }
  },
})

export const {
  useGetUserInfoQuery,
  useGetEmailChangeRequestQuery,
  useLazyGetPasswordResetTokenQuery,
  useUpdateNotificationSettingsMutation,
  useUpdateUserInfoMutation,
  useUpdateUserEmailMutation,
  useVerifyEmailMutation,
  useResendOtpMutation,
  useCancelEmailChangeRequestMutation,
  useUpdateUserPasswordMutation,
  useLazySendWelcomeEmailQuery,
} = userApi

export type UserNotificationSettingsUpdateData = {
  enable: NotificationUpdateAction[]
  disable: NotificationUpdateAction[]
}

export type UserInfoUpdateData = {
  firstname?: string
  lastname?: string
  weekendDays?: DayOfWeek[]
  firstDayOfWeek?: DayOfWeek
  timeZone?: TimeZoneType
  dateFormatSeparator?: DateSeparatorOptionType
  dateFormat?: DateOrderOptionType
  language?: Language
  featureToggles?: FeatureToggles
}

export type UserEmailUpdateData = {
  newEmail: string
}

export type UserEmailVerificationData = {
  newEmail: string
  otp: string
}

export type UserPasswordUpdateData = {
  oldPassword: string
  newPassword: string
}

type NotificationUpdateAction = { type: NotificationType; channel: NotificationChannel }
type WelcomeEmailType = "personalUse" | "organisationalUse" | "invitedMember"
type FeatureToggleKey = "enableHourReporting"

type FeatureToggles = {
  [K in FeatureToggleKey]?: boolean
}
